<template>
  <div>
    <panel>
      <div slot="toolbar">
        <w-button class="btn-light float-right" text="Wybierz kolumny" @click="openColumnsModal" />

        <w-button class="btn-light float-right mr-2" text="Wybierz filtry" @click="openFiltersModal" />
      </div>

      <w-table
        v-if="count !== undefined"
        id-key="documentId"
        :data="list"
        :columns="columns"
        :total="count"
        :hide-search-input="true"
        :hide-add-button="true"
        :per-page="-1"
        :default-sort="{ order: 'data.applicant.lastName', desc: false }"
        @view="goToDetails"
      />

      <div slot="footer" class="float-right">
        <div>
          <post-form
            text="Pobierz CSV"
            class="btn-light mr-2"
            icon="fas fa-download mr-2"
            :url="csvUrl"
            :fields="reportFields"
            :disabled="!count"
          />

          <post-form
            text="Pobierz PDF"
            class="btn-light"
            icon="fas fa-download mr-2"
            :url="pdfUrl"
            :fields="reportFields"
            :disabled="!count"
          />
        </div>
      </div>
    </panel>

    <checkbox-modal
      title="Wybierz kolumny"
      class="modal-big"
      checkbox-class="col-12 col-md-6 col-lg-4"
      :options="modalOptions"
      :selected-options="selectedColumns"
      :show="showColumnsModal"
      @close="closeColumnsModal"
      @save="saveColumns"
    />

    <filters-modal
      title="Wybierz filtry"
      class="modal-big"
      :filters="filters"
      :show="showFiltersModal"
      @close="closeFiltersModal"
      @save="saveFilters"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import CheckboxModal from '@/components/modals/CheckboxModal';
import Panel from '@/components/panel/Panel';
import PostForm from '@/components/postForm/PostForm';
import WTable from '@/components/table/Table';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Storage } from '@/enums/storage.enum';
import { notificationsMixin } from '@/mixins/notifications';
import { reportFiltersMixin } from '@/mixins/reportFilters';
import { config } from '@/utils/config';
import { parseJsonOrDefault } from '@/utils/parseJsonOrDefault';
import { getReportFilters, getReportList, getReportUnwinds } from '@/utils/report';

// @vuese
// Lista wszystkich dokumentów do raportów
// @group Reports
export default {
  components: { CheckboxModal, Panel, PostForm, WTable },
  mixins: [notificationsMixin, reportFiltersMixin()],

  data() {
    const staticColumns = [
      {
        name: 'index',
        displayName: 'LP',
        sortable: false,
        action: 'view'
      },
      {
        name: 'documentId',
        displayName: 'ID',
        sortable: true,
        action: 'view'
      }
    ];

    return {
      staticColumns,
      columns: [...staticColumns],
      csvUrl: config.csvURL + '/document-report',
      pdfUrl: config.renderURL + '/pdf/document-report',
      showColumnsModal: false
    };
  },

  computed: {
    ...mapState({
      list: state => state.documentIndex.statistics,
      availableColumns: state => JSON.parse(state.setting.settings[PublicSettings.REPORT_COLUMNS].value),
      joinChildren: state => !!state.setting.settings[PublicSettings.CHILD_PROCEDURE_ID]?.value
    }),
    defaultColumns() {
      return this.availableColumns.filter(column => column.defaultColumn);
    },
    count() {
      return this.list.length;
    },
    modalOptions() {
      return this.availableColumns.map(column => ({
        text: column.displayName,
        value: column.displayName
      }));
    },
    selectedColumns() {
      return this.columns
        .filter(column => !this.staticColumns.some(staticColumn => staticColumn.name === column.name))
        .map(column => column.displayName);
    },
    reportFields() {
      this.filters; // pola nie odświeżą się po zmianie filtrów bez tego
      return [
        { name: 'filters', value: JSON.stringify(getReportFilters(this.$route)) },
        { name: 'columns', value: JSON.stringify(this.columns) },
        { name: 'unwinds', value: JSON.stringify(getReportUnwinds(this.$route)) },
        { name: 'joinChildren', value: this.joinChildren }
      ];
    }
  },

  mounted() {
    this.columns = this.getColumns();
  },

  methods: {
    // @vuese
    // Zwraca listę wybranych kolumn do wyświetlenia
    getColumns() {
      const storageColumns = sessionStorage.getItem(Storage.REPORT_COLUMNS + this.$route.params.procedureId);
      if (storageColumns) {
        const columns = parseJsonOrDefault(storageColumns);
        if (columns) {
          return columns;
        }
        sessionStorage.removeItem(Storage.REPORT_COLUMNS + this.$route.params.procedureId);
      }
      return [...this.staticColumns, ...this.defaultColumns];
    },

    // @vuese
    // Otwiera modala z wyborem kolumn
    openColumnsModal() {
      this.showColumnsModal = true;
    },

    // @vuese
    // Zapisuje kolumny
    saveColumns(columnNames) {
      const previousUnwinds = getReportUnwinds(this.$route);
      this.columns = this.availableColumns.filter(column => columnNames.includes(column.displayName));
      this.columns.unshift(...this.staticColumns);
      sessionStorage.setItem(Storage.REPORT_COLUMNS + this.procedureId, JSON.stringify(this.columns));
      const currentUnwinds = getReportUnwinds(this.$route);
      if (previousUnwinds.lenth !== currentUnwinds.length) {
        this.updateList();
      }
      this.closeColumnsModal();
    },

    // @vuese
    // Zamyka modala z wyborem kolumn
    closeColumnsModal() {
      this.showColumnsModal = false;
    },

    // @vuese
    // Aktualizuje listę
    updateList(route = this.$route) {
      return getReportList(route);
    },
    // @vuese
    // Przenosi do szczegółów wniosku
    goToDetails(document) {
      this.$router.push({
        name: 'documentDetails',
        params: { documentId: document.documentId, procedureId: document.procedureId },
        query: this.$route.query
      });
    }
  },

  beforeRouteUpdate(to, from, next) {
    this.updateList(to)
      .then(() => next())
      .catch(err => this.notifyError(Errors.REPORT_CANNOT_GET));
  }
};
</script>
